import clsx from 'clsx';
import { motion, useReducedMotion } from 'motion/react';

import { Icon } from '@gitbook/spine-icons';
import { Button, ChatBubble, ChatColumn, ChatLine } from '@gitbook/spine-react';

import type { GitBookAgentOutboxMessage } from '../Agent';
import { AttachmentsSummary } from './AttachmentsSummary';
import { MessageDocument } from './MessageDocument';

/**
 * Messages the user posted that the agent has not acknowledged yet: sending, queued while
 * disconnected, or failed. Rendered after the conversation, styled like user messages, so a
 * submitted message stays visible from the click until the agent echoes it back — it can
 * fail, but it can never silently disappear.
 */
export function AgentChatOutbox(props: {
    outbox: GitBookAgentOutboxMessage[];
    /**
     * True while the agent is disconnected or reconnecting, to explain why entries are
     * waiting instead of showing an indefinite "Sending…".
     */
    waitingForConnection: boolean;
    /** Padding compensation when the outbox is the first content of the conversation. */
    isFirstContent: boolean;
    onRetry: (id: string) => void;
    onDiscard: (id: string) => void;
}) {
    const { outbox, waitingForConnection, isFirstContent, onRetry, onDiscard } = props;

    if (outbox.length === 0) {
        return null;
    }

    return (
        <ChatColumn className={clsx('gap-3 pb-4', isFirstContent ? 'pt-4' : null)}>
            {outbox.map((entry) => (
                <AgentChatOutboxMessage
                    key={entry.id}
                    entry={entry}
                    waitingForConnection={waitingForConnection}
                    onRetry={onRetry}
                    onDiscard={onDiscard}
                />
            ))}
        </ChatColumn>
    );
}

function AgentChatOutboxMessage(props: {
    entry: GitBookAgentOutboxMessage;
    waitingForConnection: boolean;
    onRetry: (id: string) => void;
    onDiscard: (id: string) => void;
}) {
    const { entry, waitingForConnection, onRetry, onDiscard } = props;
    const failed = entry.status === 'failed';
    const attachments = entry.message.attachments ?? [];
    const prefersReducedMotion = useReducedMotion();

    return (
        // Rises from the composer it just left into its place in the conversation — the moment a
        // blank conversation becomes one, so the movement carries the transition instead of a hard
        // swap. A restored entry replays it, which reads as the same thing: this message is still
        // on its way.
        <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
        >
            <ChatBubble
                // Dimmed until the agent acknowledges it, mirroring the usual optimistic-message
                // affordance.
                dimmed={!failed}
                footer={
                    failed ? (
                        <ChatLine
                            icon={Icon.Alert}
                            tone="warning"
                            // Ends where the bubble's text does.
                            className="pr-3"
                            trailing={
                                <>
                                    <Button
                                        size="xsmall"
                                        kind="secondary"
                                        label="Retry"
                                        onPress={() => onRetry(entry.id)}
                                    />
                                    <Button
                                        size="xsmall"
                                        kind="blank"
                                        label="Discard"
                                        onPress={() => onDiscard(entry.id)}
                                    />
                                </>
                            }
                        >
                            This message couldn't be sent.
                        </ChatLine>
                    ) : (
                        <ChatLine loading className="pr-3">
                            {waitingForConnection ? 'Waiting for connection…' : 'Sending…'}
                        </ChatLine>
                    )
                }
            >
                <MessageDocument content={entry.message.content} />
                {attachments.length > 0 ? (
                    <AttachmentsSummary attachments={attachments} size="large" />
                ) : null}
            </ChatBubble>
        </motion.div>
    );
}
